import * as configcat from "configcat-js";
import { PollingMode, type User } from "configcat-js";
import { CONFIG_CAT_KEY } from "../common/environment";
import { getConfigCatLogLevelFromStorage } from "./utils/storage/loggerStorageUtils";

// All the keys we use in ConfigCat
export enum ConfigCatKeys {
   BARCODESCANNER_AVAILABLE = "barcodeScannerAvailable",
   CLAIMS_AVAILABLE = "claimsAvailable",
   PICKUP_ORDER_AVAILABLE = "pickupOrderAvailable",
   NOTIFICATIONS_AVAILABLE = "notificationsAvailable",
   NEW_PROMOTION_NOTIFICATIONS_AVAILABLE = "newPromotionNotificationsAvailable",
   RECOMMENDATION_TYPE = "recommendationType",
   KEYFIGURES_AVAILABLE = "keyFiguresAvailable",
   CONTRACTUAL_RECOMMENDATIONS = "contractualRecommendations",
   VOUCHERIFY_AVAILABLE = "voucherifyAvailable",
   USE_DYNAMICS_FOR_NOTIFICATION_PROFILES = "useDynamicsForNotificationProfiles",
   PRIORITIZE_AGREEMENT_ASSORTMENT = "prioritizeAgreementAssortment",
   SEARCH_RESULTS_SORTED_BY_CUSTOMER_GROUP_SALES = "searchResultsSortedByCustomerGroupSales",
   NAVIGATION_TEST_VARIANT = "navigationtestvariant",
   API_PROXY_ORDERS = "apiProxyOrders",
   API_PROXY_INVOICES = "apiProxyInvoices",
   API_PROXY_FEATURES = "apiProxyFeatures",
   API_PROXY_DELIVERY_WINDOWS = "apiProxyDeliveryWindows",
   API_PROXY_NOTIFICATION_PROFILES = "apiProxyNotificationProfiles",
   FILTER_DISPLAY_THRESHOLD = "filterdisplaythreshold"
}

// Define a mapping type that associates keys with return types to allow TypeScript to
// infer the correct return type based on the key.
type ConfigCatKeyReturnType = {
   [ConfigCatKeys.BARCODESCANNER_AVAILABLE]: boolean;
   [ConfigCatKeys.CLAIMS_AVAILABLE]: boolean;
   [ConfigCatKeys.PICKUP_ORDER_AVAILABLE]: boolean;
   [ConfigCatKeys.NOTIFICATIONS_AVAILABLE]: boolean;
   [ConfigCatKeys.NEW_PROMOTION_NOTIFICATIONS_AVAILABLE]: boolean;
   [ConfigCatKeys.KEYFIGURES_AVAILABLE]: boolean;
   [ConfigCatKeys.RECOMMENDATION_TYPE]: "AI1" | "AI2";
   [ConfigCatKeys.CONTRACTUAL_RECOMMENDATIONS]: boolean;
   [ConfigCatKeys.VOUCHERIFY_AVAILABLE]: boolean;
   [ConfigCatKeys.USE_DYNAMICS_FOR_NOTIFICATION_PROFILES]: boolean;
   [ConfigCatKeys.PRIORITIZE_AGREEMENT_ASSORTMENT]: boolean;
   [ConfigCatKeys.SEARCH_RESULTS_SORTED_BY_CUSTOMER_GROUP_SALES]: boolean;
   [ConfigCatKeys.NAVIGATION_TEST_VARIANT]: "megamenu" | "sidemenu" | "topmenu";
   [ConfigCatKeys.API_PROXY_ORDERS]: boolean;
   [ConfigCatKeys.API_PROXY_INVOICES]: boolean;
   [ConfigCatKeys.API_PROXY_FEATURES]: boolean;
   [ConfigCatKeys.API_PROXY_DELIVERY_WINDOWS]: boolean;
   [ConfigCatKeys.API_PROXY_NOTIFICATION_PROFILES]: boolean;
   [ConfigCatKeys.FILTER_DISPLAY_THRESHOLD]: number;
};

// Define a mapping object with default values for each key now that we can no longer default to false
const defaultValueReturnValues = {
   [ConfigCatKeys.BARCODESCANNER_AVAILABLE]: false,
   [ConfigCatKeys.CLAIMS_AVAILABLE]: false,
   [ConfigCatKeys.PICKUP_ORDER_AVAILABLE]: false,
   [ConfigCatKeys.NOTIFICATIONS_AVAILABLE]: false,
   [ConfigCatKeys.NEW_PROMOTION_NOTIFICATIONS_AVAILABLE]: false,
   [ConfigCatKeys.KEYFIGURES_AVAILABLE]: false,
   [ConfigCatKeys.RECOMMENDATION_TYPE]: "AI2",
   [ConfigCatKeys.CONTRACTUAL_RECOMMENDATIONS]: false,
   [ConfigCatKeys.VOUCHERIFY_AVAILABLE]: false,
   [ConfigCatKeys.USE_DYNAMICS_FOR_NOTIFICATION_PROFILES]: false,
   [ConfigCatKeys.PRIORITIZE_AGREEMENT_ASSORTMENT]: false,
   [ConfigCatKeys.SEARCH_RESULTS_SORTED_BY_CUSTOMER_GROUP_SALES]: false,
   [ConfigCatKeys.NAVIGATION_TEST_VARIANT]: "megamenu",
   [ConfigCatKeys.API_PROXY_ORDERS]: false,
   [ConfigCatKeys.API_PROXY_INVOICES]: false,
   [ConfigCatKeys.API_PROXY_FEATURES]: false,
   [ConfigCatKeys.API_PROXY_DELIVERY_WINDOWS]: false,
   [ConfigCatKeys.API_PROXY_NOTIFICATION_PROFILES]: false,
   [ConfigCatKeys.FILTER_DISPLAY_THRESHOLD]: 8
};

/**
 * Set up ConfigCat client so we can do true feature toggles
 */

const logLevelFromStorage = Number.parseInt(getConfigCatLogLevelFromStorage());

const configCatClient = configcat.getClient(CONFIG_CAT_KEY || "MISSING-API-KEY", PollingMode.AutoPoll, {
   pollIntervalSeconds: 60 * 20,
   logger: configcat.createConsoleLogger(logLevelFromStorage)
});

export function getConfigCatValue<K extends ConfigCatKeys>(key: K, userData?: User): Promise<ConfigCatKeyReturnType[K]>;

/** Fetches a value from ConfigCat, with a fallback value to false if it's not available. */
export async function getConfigCatValue<K extends ConfigCatKeys>(key: K, userData?: User): Promise<ConfigCatKeyReturnType[K]> {
   const value = await configCatClient.getValueAsync(key, defaultValueReturnValues[key], userData).catch((_e) => {
      return defaultValueReturnValues[key];
   });
   if (key === ConfigCatKeys.RECOMMENDATION_TYPE || key === ConfigCatKeys.NAVIGATION_TEST_VARIANT) {
      return value as ConfigCatKeyReturnType[K];
   }
   if (key === ConfigCatKeys.FILTER_DISPLAY_THRESHOLD) {
      const num = Number(value);
      return (Number.isNaN(num) ? defaultValueReturnValues[key] : num) as ConfigCatKeyReturnType[K];
   }
   return Boolean(value) as ConfigCatKeyReturnType[K];
}
